import React from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import AdminShell from './AdminShell';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Card, CardContent } from '../../ui/card';
import { MetricCard, MetricLabel, MetricValue } from '../../ui/metric-card';
import { CAMPAIGNS, statusVariant, prettyDate } from '../fixtures';
import { Plus, ClipboardList, Users, CheckCircle2, Clock, ChevronRight, ArrowUpDown, Search } from 'lucide-react';

const StatusBadge = ({ status, label }) => (
  <Badge variant={statusVariant(status)} size='default' dot={status === 'active' || status === 'closing'}>
    {label}
  </Badge>
);

const EmptyState = () => (
  <Card className='border-dashed'>
    <CardContent className='py-16 flex flex-col items-center text-center'>
      <div className='w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-5 text-accent'>
        <ClipboardList size={22} strokeWidth={1.5} />
      </div>
      <h3 className='font-display text-xl mb-2'>No campaigns yet</h3>
      <p className='text-sm text-muted-foreground max-w-[420px] mb-6'>
        Create your first WFH &amp; Commuting survey campaign to collect employee data for Scope 3 Cat 7 reporting.
      </p>
      <Button size='lg' asChild>
        <Link to='/prototype/wfh-survey/admin/new'>
          <Plus size={16} strokeWidth={1.5} />
          Create campaign
        </Link>
      </Button>
    </CardContent>
  </Card>
);

const SurveyList = () => {
  const { search } = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(search);
  const isEmpty = params.get('state') === 'empty';

  const totals = {
    active: CAMPAIGNS.filter((c) => c.status === 'active').length,
    closed: CAMPAIGNS.filter((c) => c.status === 'closed').length,
    responses: CAMPAIGNS.reduce((a, c) => a + c.completedCount, 0),
    invited: CAMPAIGNS.reduce((a, c) => a + c.invitedCount, 0),
  };

  return (
    <AdminShell
      breadcrumbs={[{ label: 'Sustainability' }, { label: 'Surveys' }]}
      eyebrow='Surveys'
      title='WFH & Commuting Campaigns'
      description='Collect employee working-from-home and commuting data for your Scope 3 Category 7 footprint. Each campaign represents one reporting period.'
      actions={
        <>
          <Button
            variant='outline'
            size='default'
            onClick={() => history.push(`/prototype/wfh-survey/admin/list${isEmpty ? '' : '?state=empty'}`)}>
            Toggle state ({isEmpty ? 'empty' : 'populated'})
          </Button>
          <Button size='default' asChild>
            <Link to='/prototype/wfh-survey/admin/new'>
              <Plus size={16} strokeWidth={1.5} />
              New campaign
            </Link>
          </Button>
        </>
      }>
      {isEmpty ? (
        <EmptyState />
      ) : (
        <>
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
            <MetricCard>
              <MetricLabel>Active campaigns</MetricLabel>
              <MetricValue>{totals.active}</MetricValue>
              <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
                <Clock size={12} strokeWidth={1.5} />
                Closing in 14 days
              </div>
            </MetricCard>
            <MetricCard accent='success'>
              <MetricLabel>Closed this FY</MetricLabel>
              <MetricValue>{totals.closed}</MetricValue>
              <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
                <CheckCircle2 size={12} strokeWidth={1.5} />
                Results available
              </div>
            </MetricCard>
            <MetricCard accent='amber'>
              <MetricLabel>Responses collected</MetricLabel>
              <MetricValue>{totals.responses.toLocaleString()}</MetricValue>
              <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
                <Users size={12} strokeWidth={1.5} />
                across {totals.invited.toLocaleString()} invited
              </div>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Average response rate</MetricLabel>
              <MetricValue unit='%'>{Math.round((totals.responses / totals.invited) * 100)}</MetricValue>
              <div className='text-xs text-success flex items-center gap-1.5 mt-2'>
                <span>&#9650;</span>
                +12pp vs manual process
              </div>
            </MetricCard>
          </div>

          <Card>
            <div className='flex items-center gap-2 px-4 py-3 border-b border-border'>
              <div className='relative flex-1 max-w-sm'>
                <Search
                  size={14}
                  strokeWidth={1.5}
                  className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
                />
                <input
                  type='text'
                  placeholder='Search campaigns...'
                  className='w-full pl-9 pr-3 py-1.5 text-sm rounded-md border border-input bg-transparent focus:outline-none focus:border-primary'
                />
              </div>
              <Button variant='ghost' size='sm'>
                <ArrowUpDown size={14} strokeWidth={1.5} />
                Sort
              </Button>
            </div>
            <Table wrapperClassName='rounded-none border-0 shadow-none'>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reporting period</TableHead>
                  <TableHead>Deadline</TableHead>
                  <TableHead className='text-right'>Response rate</TableHead>
                  <TableHead className='text-right'>Completed</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {CAMPAIGNS.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    className='cursor-pointer'
                    onClick={() => history.push(`/prototype/wfh-survey/admin/campaign/${campaign.id}`)}>
                    <TableCell className='font-medium'>
                      <div>{campaign.name}</div>
                      <div className='text-xs text-muted-foreground mt-0.5'>
                        Created {prettyDate(campaign.createdAt)} by {campaign.createdBy}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={campaign.status} label={campaign.statusLabel} />
                    </TableCell>
                    <TableCell className='text-sm'>{campaign.reportingPeriod}</TableCell>
                    <TableCell className='text-sm'>{prettyDate(campaign.deadline)}</TableCell>
                    <TableCell className='text-right tabular-nums'>
                      {campaign.status === 'draft' ? (
                        <span className='text-muted-foreground'>—</span>
                      ) : (
                        <span className='font-mono text-sm'>{campaign.responseRate}%</span>
                      )}
                    </TableCell>
                    <TableCell className='text-right tabular-nums'>
                      {campaign.status === 'draft' ? (
                        <span className='text-muted-foreground'>—</span>
                      ) : (
                        <span className='font-mono text-sm'>
                          {campaign.completedCount} / {campaign.invitedCount}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className='text-right'>
                      <ChevronRight size={16} strokeWidth={1.5} className='text-muted-foreground' />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </AdminShell>
  );
};

export default SurveyList;
